import styled from '@emotion/styled'
import {
    ApiPdpSellingPlan,
    ApiPdpSellingPlanGroup,
} from '@storefronts/common-types'
import { useContext, useId, useState } from 'react'

import {
    CustomizationContext,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SUBTLE_PRIMARY_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import { getTypographyRoleStyle } from '../../../style/typography'
import {
    getPdpAdjustedPriceCents,
    getPdpFrequencyLabel,
    getPdpSavingsPercent,
    getPdpSubscriptionLabel,
    getSubscribablePdpPlans,
} from '../../../utils/pdpSubscriptions'
import { priceFormat } from '../../../utils/priceFormat'
import { useSubscriptionOptions } from '../../../utils/subscriptions'
import { scaledFontSize } from '../../../style/scrollingLayout'
import { onHover } from '../../../style/interactiveStates'

// Extra gap so the subscribe card's "Save X%" pill, which straddles the top
// border, doesn't crowd the one-time card above it.
const OptionGroup = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    marginTop: 16,
})

// The card corner radius honors the brand's SQUARE choice (no radius) but keeps
// the soft card radius otherwise, since the button-specific ROUNDED/PILL radii
// don't translate well to a full-width card.
const CARD_BORDER_RADIUS = 12

const UNSELECTED_OPTION_BORDER = `color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) 25%, transparent)`

// Both states use a 2px border so selecting never shifts the layout; only the
// color changes between page text (selected) and a lighter mix (unselected).
const OptionCard = styled.label<{ selected: boolean; borderRadius: number }>(
    ({ selected, borderRadius }) => ({
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        boxSizing: 'border-box',
        padding: 16,
        borderRadius,
        cursor: 'pointer',
        background: 'none',
        color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
        border: `2px solid ${
            selected
                ? `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`
                : UNSELECTED_OPTION_BORDER
        }`,
        transition: 'border-color 0.15s ease',
        ...(!selected &&
            onHover({
                borderColor: `color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) 55%, transparent)`,
            })),
        // Keyboard-only focus ring (not shown on tap) so keyboard users can see
        // which option is focused.
        '&:has(input:focus-visible)': {
            outline: `2px solid var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
            outlineOffset: 2,
        },
    }),
)

const SavePill = styled.span({
    position: 'absolute',
    top: 0,
    left: 16,
    transform: 'translateY(-50%)',
    padding: '2px 10px',
    borderRadius: 999,
    background: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    color: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    whiteSpace: 'nowrap',
    ...getTypographyRoleStyle('badgeText', { fontWeight: 600 }),
    fontSize: scaledFontSize(12),
})

const HiddenRadio = styled.input({
    position: 'absolute',
    width: 1,
    height: 1,
    opacity: 0,
    margin: 0,
    pointerEvents: 'none',
})

const MainRow = styled.div({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    width: '100%',
})

const RadioDot = styled.span<{ selected: boolean }>(({ selected }) => ({
    flexShrink: 0,
    width: 20,
    height: 20,
    marginTop: 1,
    borderRadius: '50%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Matches the card's border color so the radio and its card read as one.
    border: `2px solid ${
        selected
            ? `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`
            : UNSELECTED_OPTION_BORDER
    }`,
    transition: 'border-color 0.15s ease',
    '&::after': {
        content: '""',
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
        opacity: selected ? 1 : 0,
        transition: 'opacity 0.15s ease',
    },
}))

const OptionBody = styled.div({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
})

const OptionTopRow = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    width: '100%',
})

const Headline = styled.span({
    ...getTypographyRoleStyle('labelText', { fontWeight: 600 }),
    fontSize: scaledFontSize(15),
})

// One-time price struck through to the left of the resulting subscription
// price.
const PriceBlock = styled.span({
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 6,
    flexShrink: 0,
})

const PriceValue = styled.span({
    ...getTypographyRoleStyle('price', { fontWeight: 600 }),
    fontSize: scaledFontSize(15),
})

const StruckPrice = styled.span({
    ...getTypographyRoleStyle('compareAtPrice'),
    fontSize: scaledFontSize(13),
    color: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
    textDecoration: 'line-through',
})

// Aligned to the card's left padding (same x as the radio), not indented under
// the headline.
const FrequencyRow = styled.div({
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    marginTop: 10,
})

const FrequencyLabel = styled.span({
    ...getTypographyRoleStyle('bodyText'),
    fontSize: scaledFontSize(13),
})

const FrequencyValueWrapper = styled.span({
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'baseline',
})

const FrequencyValue = styled.span({
    ...getTypographyRoleStyle('labelText', { fontWeight: 600 }),
    fontSize: scaledFontSize(13),
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
})

// Transparent native picker overlaid on the frequency text: taps open the
// OS-native option list on mobile while the styled text stays visible.
const FrequencySelect = styled.select({
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    margin: 0,
    padding: 0,
    border: 'none',
    cursor: 'pointer',
    // 16px avoids the iOS focus zoom even though the control is invisible.
    fontSize: scaledFontSize(16),
})

type FrequencyPickerProps = {
    plans: ApiPdpSellingPlan[]
    chosenPlan: ApiPdpSellingPlan
    defaultPlanId: string | null
    onChangeFrequency: (planId: string) => void
}

const FrequencyPicker: React.FC<FrequencyPickerProps> = ({
    plans,
    chosenPlan,
    defaultPlanId,
    onChangeFrequency,
}) => (
    <FrequencyRow>
        <FrequencyLabel>Deliver every:&nbsp;</FrequencyLabel>
        <FrequencyValueWrapper>
            <FrequencyValue>
                {getPdpFrequencyLabel(chosenPlan)}
                {chosenPlan.id === defaultPlanId ? ' (most common)' : ''}
            </FrequencyValue>
            {plans.length > 1 && (
                <FrequencySelect
                    aria-label="Delivery frequency"
                    value={chosenPlan.id}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onChangeFrequency(e.target.value)}
                >
                    {plans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                            {getPdpFrequencyLabel(plan)}
                            {plan.id === defaultPlanId ? ' (most common)' : ''}
                        </option>
                    ))}
                </FrequencySelect>
            )}
        </FrequencyValueWrapper>
    </FrequencyRow>
)

type SubscribeCardBodyProps = {
    plans: ApiPdpSellingPlan[]
    chosenPlan: ApiPdpSellingPlan
    defaultPlanId: string | null
    subscriptionBase: number
    currencySymbol: string | undefined
    headline: string
    radioSelected: boolean
    onChangeFrequency: (planId: string) => void
}

const SubscribeCardBody: React.FC<SubscribeCardBodyProps> = ({
    plans,
    chosenPlan,
    defaultPlanId,
    subscriptionBase,
    currencySymbol,
    headline,
    radioSelected,
    onChangeFrequency,
}) => {
    const subscriptionPriceCents =
        getPdpAdjustedPriceCents(subscriptionBase, chosenPlan) ??
        subscriptionBase
    const savingsPercent = getPdpSavingsPercent(subscriptionBase, chosenPlan)

    return (
        <>
            {savingsPercent != null && (
                <SavePill>Save {savingsPercent}%</SavePill>
            )}
            <MainRow>
                <RadioDot selected={radioSelected} aria-hidden="true" />
                <OptionBody>
                    <OptionTopRow>
                        <Headline>{headline}</Headline>
                        <PriceBlock>
                            {savingsPercent != null && (
                                <StruckPrice>
                                    {priceFormat(
                                        subscriptionBase,
                                        currencySymbol,
                                    )}
                                </StruckPrice>
                            )}
                            <PriceValue>
                                {priceFormat(
                                    subscriptionPriceCents,
                                    currencySymbol,
                                )}
                            </PriceValue>
                        </PriceBlock>
                    </OptionTopRow>
                </OptionBody>
            </MainRow>
            <FrequencyPicker
                plans={plans}
                chosenPlan={chosenPlan}
                defaultPlanId={defaultPlanId}
                onChangeFrequency={onChangeFrequency}
            />
        </>
    )
}

/**
 * One-time purchase vs subscription choice on PDPs / inline products,
 * applied when the item is added to the cart (unlike CartSubscription,
 * which edits an existing cart line). Subscription-only products have no
 * purchase choice to make, so this renders nothing for them — the
 * subscription still applies via the default selling plan (see
 * `useProductData`), it's just not surfaced as a selectable option.
 */
export const PdpSubscriptionSelector: React.FC<{
    sellingPlanGroups?: ApiPdpSellingPlanGroup[]
    /** One-time purchase price of the active variant (promo applied), in cents. */
    priceCents: number | null
    /**
     * The variant's own price that the selling plan discounts, in cents.
     * Shopify applies the plan to this (not a promo layered on top), so the
     * subscription price and its strikethrough are derived from it to match the
     * cart. Falls back to the one-time price when not provided.
     */
    subscriptionBasePriceCents?: number | null
    selectedSellingPlanId: string | null
    /** When true, this product has no one-time purchase option. */
    requiresSellingPlan?: boolean
    onSelect: (sellingPlanId: string | null) => void
}> = ({
    sellingPlanGroups,
    priceCents,
    subscriptionBasePriceCents,
    selectedSellingPlanId,
    requiresSellingPlan = false,
    onSelect,
}) => {
    const { landingPage } = useContext(ViddyLPContext)
    const { buttonBorderRadiusStyle, displaySubscriptionOptions } =
        useContext(CustomizationContext)
    const subscriptionOptions = useSubscriptionOptions()
    const cardBorderRadius =
        buttonBorderRadiusStyle === 'SQUARE' ? 0 : CARD_BORDER_RADIUS
    const subscriptionBase = subscriptionBasePriceCents ?? priceCents
    const plans = getSubscribablePdpPlans(sellingPlanGroups, subscriptionBase)
    const defaultPlanId = plans[0]?.id ?? null
    const [chosenPlanId, setChosenPlanId] = useState<string | null>(
        defaultPlanId,
    )
    // Unique per instance so multiple inline PDPs on one page don't share a
    // native radio group (which would let one product's choice clear another's).
    const radioGroupName = useId()

    // Gated by the brand's subscription-options setting. When disabled no plan
    // can be selected, so the rest of the PDP subscription flow stays inert.
    if (
        !displaySubscriptionOptions ||
        requiresSellingPlan ||
        !plans.length ||
        priceCents == null ||
        subscriptionBase == null
    ) {
        return null
    }

    const currencySymbol = landingPage?.client?.currencySymbol
    // Treat a subscription as selected only when the id resolves to a
    // subscribable plan, matching how the hero derives its price. Guards against
    // a selected id that isn't subscribable for the current variant leaving the
    // subscribe card selected while the hero shows one-time pricing.
    const isOneTimeSelected = !plans.some(
        (plan) => plan.id === selectedSellingPlanId,
    )
    // When a subscription is active, follow the parent's selected plan so the
    // card, hero price, and cart stay in sync; the remembered local choice only
    // drives the preview shown while one-time is selected.
    const activePlanId = selectedSellingPlanId ?? chosenPlanId
    const chosenPlan =
        plans.find((plan) => plan.id === activePlanId) ?? plans[0]
    const headline = getPdpSubscriptionLabel(
        plans,
        subscriptionBase,
        subscriptionOptions?.subscriptionButtonText,
    )

    const selectSubscription = () => {
        setChosenPlanId(chosenPlan.id)
        onSelect(chosenPlan.id)
    }

    const changeFrequency = (planId: string) => {
        setChosenPlanId(planId)
        onSelect(planId)
    }

    const subscribeBody = (
        <SubscribeCardBody
            plans={plans}
            chosenPlan={chosenPlan}
            defaultPlanId={defaultPlanId}
            subscriptionBase={subscriptionBase}
            currencySymbol={currencySymbol}
            headline={headline}
            radioSelected={!isOneTimeSelected}
            onChangeFrequency={changeFrequency}
        />
    )

    return (
        <OptionGroup role="radiogroup" aria-label="Purchase options">
            <OptionCard
                selected={isOneTimeSelected}
                borderRadius={cardBorderRadius}
            >
                <HiddenRadio
                    type="radio"
                    name={radioGroupName}
                    checked={isOneTimeSelected}
                    onChange={() => onSelect(null)}
                />
                <MainRow>
                    <RadioDot selected={isOneTimeSelected} aria-hidden="true" />
                    <OptionBody>
                        <OptionTopRow>
                            <Headline>One-time purchase</Headline>
                            <PriceValue>
                                {priceFormat(priceCents, currencySymbol)}
                            </PriceValue>
                        </OptionTopRow>
                    </OptionBody>
                </MainRow>
            </OptionCard>

            <OptionCard
                selected={!isOneTimeSelected}
                borderRadius={cardBorderRadius}
            >
                <HiddenRadio
                    type="radio"
                    name={radioGroupName}
                    checked={!isOneTimeSelected}
                    onChange={selectSubscription}
                />
                {subscribeBody}
            </OptionCard>
        </OptionGroup>
    )
}
